import React, { useState, useEffect, useMemo } from 'react';
import {
  FaCheckCircle, FaSpinner, FaTimes, FaUserShield,
  FaUserTie, FaCalendarAlt, FaSearch,
} from 'react-icons/fa';
import './ModalAprovarConta.css';

const fmtCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const todayISO = () => new Date().toISOString().split('T')[0];

/**
 * [APROVAÇÃO] Modal de aprovação de reembolso (A/P → A/R espelho).
 * Suporta tanto aprovação individual quanto em lote:
 *   - `contas` é sempre um array (length 1 = individual, >1 = lote).
 * O caller decide qual endpoint chamar (/aprovar ou /aprovar-lote)
 * a partir de `onConfirm` — este componente só coleta pagador + data.
 */
export default function ModalAprovarConta({
  isOpen,
  contas = [],
  pagadores = [],
  aprovadorNome,
  submitting = false,
  onClose,
  onConfirm,
}) {
  const [busca,       setBusca]       = useState('');
  const [pagadorSel,  setPagadorSel]  = useState(null);
  const [dataPgto,    setDataPgto]    = useState(todayISO());

  const isLote     = contas.length > 1;
  const totalValor = useMemo(
    () => contas.reduce((acc, c) => acc + parseFloat(c?.valor || 0), 0),
    [contas]
  );

  // Reset ao abrir/trocar de seleção
  useEffect(() => {
    if (isOpen) {
      setBusca('');
      setPagadorSel(null);
      setDataPgto(todayISO());
    }
  }, [isOpen, contas.map(c => c?.id).join(',')]);

  const pagadoresFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return pagadores;
    return pagadores.filter(p =>
      p.nome?.toLowerCase().includes(q) ||
      p.documento?.toLowerCase().includes(q)
    );
  }, [busca, pagadores]);

  if (!isOpen || contas.length === 0) return null;

  const podeConfirmar = !!pagadorSel && !!dataPgto && !submitting;

  const handleConfirm = () => {
    if (!podeConfirmar) return;
    onConfirm({ pagador_id: pagadorSel.id, data_pagamento: dataPgto });
  };

  return (
    <div
      className="apr-overlay"
      onClick={e => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="apr-modal">
        {/* Header */}
        <div className="apr-head">
          <div className="apr-head-icon"><FaCheckCircle /></div>
          <div className="apr-head-text">
            <h3>{isLote ? `Aprovar ${contas.length} Contas` : 'Aprovar Conta'}</h3>
            <p>
              {isLote
                ? 'Reembolso em lote · vira recebível ao aprovar'
                : 'Reembolso · vira recebível ao aprovar'}
            </p>
          </div>
          <button className="apr-close" onClick={onClose} disabled={submitting}>
            <FaTimes />
          </button>
        </div>

        {/* Valor + descrição (individual) ou resumo + lista (lote) */}
        {isLote ? (
          <div className="apr-summary apr-summary-lote">
            <div className="apr-summary-row">
              <span className="apr-label">Total a reembolsar ({contas.length} contas)</span>
              <strong className="apr-value">{fmtCurrency(totalValor)}</strong>
            </div>
            <div className="apr-lote-list">
              {contas.map(c => (
                <div key={c.id} className="apr-lote-item">
                  <span className="apr-lote-desc" title={c.descricao}>{c.descricao}</span>
                  <span className="apr-lote-valor">{fmtCurrency(c.valor)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="apr-summary">
            <div className="apr-summary-row">
              <span className="apr-label">Valor a reembolsar</span>
              <strong className="apr-value">{fmtCurrency(contas[0]?.valor)}</strong>
            </div>
            <p className="apr-desc" title={contas[0]?.descricao}>{contas[0]?.descricao}</p>
          </div>
        )}

        {/* Aprovador (read-only = usuário logado) */}
        <div className="apr-field">
          <label><FaUserShield /> Aprovador</label>
          <div className="apr-aprovador">{aprovadorNome || 'Você (financeiro)'}</div>
        </div>

        {/* Data de pagamento (vencimento da A/R — aplicada a todas no lote) */}
        <div className="apr-field">
          <label>
            <FaCalendarAlt /> Data de pagamento
            {isLote && <span className="apr-field-hint"> · aplicada a todas as contas selecionadas</span>}
          </label>
          <input
            type="date"
            value={dataPgto}
            onChange={e => setDataPgto(e.target.value)}
            disabled={submitting}
          />
        </div>

        {/* Pagador (quem reembolsa — o mesmo para todas no lote) */}
        <div className="apr-field">
          <label>
            <FaUserTie /> Pagador
            {isLote && <span className="apr-field-hint"> · mesmo pagador para todas</span>}
          </label>
          <div className="apr-search">
            <FaSearch className="apr-search-icon" />
            <input
              type="text"
              placeholder="Buscar pagador por nome ou documento…"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="apr-pagador-list">
            {pagadoresFiltrados.length === 0 ? (
              <div className="apr-pagador-empty">
                {pagadores.length === 0
                  ? 'Nenhum pagador cadastrado.'
                  : 'Nenhum pagador para essa busca.'}
              </div>
            ) : pagadoresFiltrados.map(p => (
              <button
                key={p.id}
                type="button"
                className={`apr-pagador-item ${pagadorSel?.id === p.id ? 'sel' : ''}`}
                onClick={() => setPagadorSel(p)}
                disabled={submitting}
              >
                <span className="apr-pagador-avatar">
                  {p.nome?.charAt(0).toUpperCase() || '?'}
                </span>
                <span className="apr-pagador-nome">{p.nome}</span>
                {p.documento && (
                  <span className="apr-pagador-doc">{p.documento}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Ações */}
        <div className="apr-actions">
          <button className="apr-btn-cancel" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            className="apr-btn-confirm"
            onClick={handleConfirm}
            disabled={!podeConfirmar}
            title={!pagadorSel ? 'Selecione um pagador' : 'Aprovar e gerar recebível'}
          >
            {submitting ? <FaSpinner className="apr-spin" /> : <FaCheckCircle />}
            {isLote ? `Aprovar ${contas.length} contas` : 'Aprovar conta'}
          </button>
        </div>
      </div>
    </div>
  );
}